import React, { useState, useRef, useCallback, useEffect } from 'react';
import { debounce } from 'lodash';
import makeAnimated from 'react-select/animated';
import AsyncSelect from 'react-select/async';
import { components } from 'react-select';
import { navigate, useLocation } from '@reach/router';

import colors from 'styles/colors';

import Avatar from 'components/Avatar/Avatar';
import { ReactComponent as SearchIcon } from 'assets/images/icons/search/search.svg';
import CountrySelect from 'components/SelectCountry/SelectCountry';
import { GlobalCharityAsyncSelectStyles } from './GlobalCharityAsyncSelectStyles';
import classNames from 'classnames';
import useSearchStore from 'stores/SearchStoreV2';
import CauzeMatchBadge from 'components/CauzeMatchBadge/CauzeMatchBadge';

const countryWhitelist = [
  { name: 'United States', value: 'United States', icon: 'us' },
  { name: 'Canada', value: 'Canada', icon: 'canada' },
  { name: 'Global', value: 'Global', icon: 'global' },
];

const getSeeAllLabel = (inputValue, location) => {
  if (inputValue && location) {
    return `See all results for "${inputValue}" in "${location}"`;
  } else if (inputValue) {
    return `See all results for "${inputValue}"`;
  } else if (location) {
    return `See all results in "${location}"`;
  }
  return 'See all results';
};

const animatedComponents = makeAnimated();

const MultiValueContainer = (props) => (
  <components.MultiValueContainer {...props}>
    <div className="flex-row">
      {props.data.avatar && (
        <div
          style={{
            justifySelf: 'stretch',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Avatar
            xsm
            style={{ marginLeft: '0.25rem' }}
            avatar={props.data.avatar}
          />
          {props.data}
        </div>
      )}
      {props.children}
    </div>
  </components.MultiValueContainer>
);

const Menu = (props) => {
  const inputValue = props.selectProps.inputValue;
  const location = props.location;
  const searchStore = useSearchStore();

  return (
    <>
      <components.Menu {...props}>
        {props.children}
        {Boolean(props.options.length) && (
          <div className="flex justify-center w-full bg-lightgray-f1f">
            <div
              className="w-full font-agenda text-gray-1b2 text-center py-4 !h-12 hover:cursor-pointer"
              onClick={(event) => {
                event.preventDefault();
                props.selectProps.onMenuClose();

                searchStore.updateSearch({
                  query: inputValue,
                });

                navigate('/donate/search');
              }}
            >
              {inputValue && location ? (
                <>
                  See all results for{' '}
                  <span className="font-agenda-bold">"{inputValue}"</span> in
                  City/Zip Code{' '}
                  <span className="font-agenda-bold">"{location}"</span>
                </>
              ) : inputValue ? (
                <>
                  See all results for{' '}
                  <span className="font-agenda-bold">"{inputValue}"</span>
                </>
              ) : location ? (
                <>
                  See all results for City/Zip Code{' '}
                  <span className="font-agenda-bold">"{location}"</span>
                </>
              ) : (
                'See all results'
              )}
            </div>
          </div>
        )}
      </components.Menu>
    </>
  );
};

const Option = (props) => {
  return (
    <components.Option {...props}>
      <div className="flex-row flex-align-center">
        <Avatar
          sm
          style={{ marginRight: '0.5rem' }}
          avatar={props.data.avatar}
          type="charity"
        />
        <div className="flex-column">
          <div className="flex gap-x-2">
            {props.children}
            <CauzeMatchBadge charity={props.data} />
          </div>
          {props.data.ein && (
            <div className="text-[0.8rem] text-gray-500">
              EIN: {props.data.ein} | {props.data.city}, {props.data.state}
            </div>
          )}
        </div>
      </div>
    </components.Option>
  );
};

const CharitySearch = (props) => {
  const searchStore = useSearchStore();
  const loc = useLocation();
  const [value, setValue] = useState(props.defaultValue);
  const [inputValue, setInputValue] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [location, setLocation] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('United States');

  const isSearchPage = loc.pathname.includes('/donate/search');

  const searchContainerRef = useRef();

  const fetchResults = useCallback(
    async (inputValue, callback) => {
      const filters = {
        query: inputValue,
        location: location,
        country: selectedCountry,
        categories: [],
      };

      if (inputValue || location) {
        try {
          const seeAllLabel = getSeeAllLabel(inputValue, location);
          const searchCharity = await searchStore.updateAndRunSearch(filters);

          const results = searchCharity
            .map((charity) => ({
              ...charity,
              value: charity.id,
              label: charity.name,
            }))
            .concat([
              {
                value: 'see-all-results',
                label: seeAllLabel,
              },
            ]);
          callback(results);
        } catch (err) {
          console.error(err);
        }
      } else {
        callback([]);
      }
    },
    [location, selectedCountry, searchStore, loc],
  );

  const debouncedFetchResults = useCallback(debounce(fetchResults, 300), [
    fetchResults,
  ]);

  const loadOptions = useCallback(
    (inputValue, callback) => {
      debouncedFetchResults(inputValue, callback);
    },
    [debouncedFetchResults],
  );

  const handleInputChange = useCallback(
    (newValue, { action }) => {
      if (action === 'input-change') {
        setInputValue(newValue || '');
        setDropdownOpen(Boolean(newValue));

        if (!newValue && searchStore.currentSearch.query) {
          searchStore.updateSearch({
            query: '',
          });

          if (isSearchPage) {
            searchStore.getCharities();
          }
        }
      }
      return newValue;
    },
    [searchStore],
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();

        setDropdownOpen(false);

        // Close mobile keyboard
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        searchStore.updateAndRunSearch({
          query: inputValue,
          location: location,
          country: selectedCountry,
        });
        navigate('/donate/search');
      }
    },
    [inputValue, location, selectedCountry],
  );

  const handleChange = useCallback((newValue) => {
    if (newValue?.id) {
      searchStore.updateSearch({
        query: '',
      });
      setInputValue('');
      setValue(null);
      navigate(`/charity/${newValue?.id}`);
    }
  }, []);

  const handleCountryChange = useCallback((newValue) => {
    setSelectedCountry(newValue);
  }, []);

  const handleLocationChange = useCallback(
    (e) => {
      setLocation(e.target.value);
      setInputValue(inputValue);
      setDropdownOpen(true);
    },
    [inputValue],
  );

  const CustomClearIndicator = useCallback((props) => {
    return (
      <components.ClearIndicator {...props}>
        <span style={{ cursor: 'pointer', color: '#171644' }}>✕</span>
      </components.ClearIndicator>
    );
  }, []);

  useEffect(() => {
    setInputValue(searchStore.currentSearch.query);
  }, [searchStore.currentSearch.query]);

  const asyncSelectKey = `${location}-${selectedCountry}`;

  return (
    <>
      <div
        className={classNames('w-full bg-white search-container relative')}
        id="search-container"
        ref={searchContainerRef}
      >
        <div className=" flex justify-center items-center max-[380px]:px-2 px-4  py-3.5 ">
          <SearchIcon />
        </div>
        <AsyncSelect
          key={asyncSelectKey}
          value={value}
          placeholder="Search Cauze"
          loadOptions={loadOptions}
          defaultOptions
          cacheOptions={false}
          theme={(theme) => ({
            ...theme,
            colors: {
              ...theme.colors,
              primary: colors.baseColor,
              primary50: colors.lightGrey,
              primary25: colors.white,
              danger: colors.baseColor,
            },
          })}
          menuIsOpen={dropdownOpen && inputValue && !isSearchPage}
          inputValue={inputValue}
          styles={GlobalCharityAsyncSelectStyles}
          onInputChange={handleInputChange}
          components={{
            ...animatedComponents,
            Option,
            Menu: (props) => <Menu {...props} location={location} />,
            MultiValueContainer,
            ClearIndicator: CustomClearIndicator,
            LoadingIndicator: () => null,
            DropdownIndicator: () => null,
            IndicatorSeparator: () => null,
          }}
          isClearable={false}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onMenuClose={() => setDropdownOpen(false)}
          onMenuOpen={() => setDropdownOpen(true)}
          menuPortalTarget={document.getElementById('search-container')}
        />
        <input
          className="search-input-location"
          type="search"
          name="location"
          value={location}
          autoComplete="off"
          placeholder={'City/Zip Code'}
          onChange={handleLocationChange}
          onKeyDown={handleKeyDown}
        />
        <CountrySelect
          options={countryWhitelist}
          selectedValue={selectedCountry}
          onChange={handleCountryChange}
        />
        {props.showSelectionList && value && (
          <div className="search-result-container">
            {value?.map((charity) => (
              <div
                key={`${charity?.id}`}
                className="flex-row flex-align-center search-result-row font-agenda"
              >
                <Avatar avatar={charity.avatar} entityType={'CHARITY'} />
                <div className="flex-expand flex-column">
                  <div className="flex-row font-agenda-bold">
                    {charity.name}
                  </div>
                  <div className="flex-row result-detail">
                    {charity.location} | EIN: {charity.ein}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default CharitySearch;
